import { useState, useEffect } from "react";
import { X, Eye, EyeOff, Lock, Shield, Users, Phone } from "lucide-react";
import { getProfile, updateProfile } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function VisibilityDialog({ open, onClose }) {
  const { user, refresh } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [profileHidden, setProfileHidden] = useState(false);
  const [visibleTo, setVisibleTo] = useState("all");
  const [lockAllImages, setLockAllImages] = useState(false);
  const [hideFromMinAge, setHideFromMinAge] = useState("");
  const [hideFromMaxAge, setHideFromMaxAge] = useState("");
  const [hideFromHealthStatuses, setHideFromHealthStatuses] = useState("");
  const [phoneVisibility, setPhoneVisibility] = useState("matches_and_requests");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getProfile()
      .then(profile => {
        // Treat 0 and 100 as sentinels for "empty"
        const minVal = profile.hide_from_min_age;
        const maxVal = profile.hide_from_max_age;

        setProfileHidden(profile.profile_hidden || false);
        setVisibleTo(profile.visible_to || "all");
        setLockAllImages(profile.lock_all_images || false);
        setHideFromMinAge(minVal === 0 || minVal == null ? "" : minVal);
        setHideFromMaxAge(maxVal === 100 || maxVal == null ? "" : maxVal);
        setHideFromHealthStatuses(profile.hide_from_health_statuses || "");
        setPhoneVisibility(profile.phone_visibility || "matches_and_requests");
      })
      .catch(() => toast.error("Failed to load visibility settings"))
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProfile({
        profile_hidden: profileHidden,
        visible_to: visibleTo,
        lock_all_images: lockAllImages,
        // Send 0 and 100 as sentinels instead of null so the backend clears them
        hide_from_min_age: hideFromMinAge ? parseInt(hideFromMinAge) : 0,
        hide_from_max_age: hideFromMaxAge ? parseInt(hideFromMaxAge) : 100,
        hide_from_health_statuses: hideFromHealthStatuses,
        phone_visibility: phoneVisibility,
      });
      await refresh();
      toast.success("Visibility settings updated");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save visibility settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-[#E7E5E0]">
          <h2 className="font-display text-lg font-semibold flex items-center gap-2">
            <Eye className="w-5 h-5" /> Visibility
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-[#F5F3EE] rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-6 h-6 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <EyeOff className="w-4 h-4 text-gray-500" />
                  <span className="text-sm">Hide my profile from everyone</span>
                </div>
                <button
                  onClick={() => setProfileHidden(!profileHidden)}
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    profileHidden ? "bg-red-500 text-white" : "bg-gray-200 text-gray-700"
                  }`}
                >
                  {profileHidden ? "Hidden" : "Visible"}
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-gray-500" />
                  <span className="text-sm">Visible only to verified users</span>
                </div>
                <button
                  onClick={() => setVisibleTo(visibleTo === "verified_only" ? "all" : "verified_only")}
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    visibleTo === "verified_only" ? "bg-green-500 text-white" : "bg-gray-200 text-gray-700"
                  }`}
                >
                  {visibleTo === "verified_only" ? "On" : "Off"}
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-gray-500" />
                  <span className="text-sm">Lock all images for free viewers</span>
                </div>
                <button
                  onClick={() => setLockAllImages(!lockAllImages)}
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    lockAllImages ? "bg-red-500 text-white" : "bg-gray-200 text-gray-700"
                  }`}
                >
                  {lockAllImages ? "Locked" : "Unlocked"}
                </button>
              </div>

              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase text-gray-500">
                  Hide from people younger than
                </label>
                <input
                  type="number"
                  min={18}
                  max={99}
                  value={hideFromMinAge}
                  onChange={(e) => setHideFromMinAge(e.target.value)}
                  className="neo-input w-full"
                  placeholder="e.g. 21"
                />
                <label className="text-xs font-semibold uppercase text-gray-500 mt-2">
                  Hide from people older than
                </label>
                <input
                  type="number"
                  min={18}
                  max={99}
                  value={hideFromMaxAge}
                  onChange={(e) => setHideFromMaxAge(e.target.value)}
                  className="neo-input w-full"
                  placeholder="e.g. 60"
                />
              </div>

              <div>
                <label className="text-xs font-semibold uppercase text-gray-500">
                  Hide from people with these health statuses
                </label>
                <input
                  type="text"
                  value={hideFromHealthStatuses}
                  onChange={(e) => setHideFromHealthStatuses(e.target.value)}
                  className="neo-input mt-1 w-full"
                  placeholder="HIV, HPV (comma separated)"
                />
              </div>

              <div className="pt-2 border-t border-gray-100">
                <h3 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-500" /> Phone Number Visibility
                </h3>
                <p className="text-xs text-gray-500 mb-3">
                  Choose who can see your WhatsApp number.
                </p>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="phoneVisibility"
                      value="matches_and_requests"
                      checked={phoneVisibility === "matches_and_requests"}
                      onChange={() => setPhoneVisibility("matches_and_requests")}
                      className="text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm">Matches and Users who have requested (pending)</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="phoneVisibility"
                      value="matches"
                      checked={phoneVisibility === "matches"}
                      onChange={() => setPhoneVisibility("matches")}
                      className="text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm">Only my matches</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="phoneVisibility"
                      value="requests"
                      checked={phoneVisibility === "requests"}
                      onChange={() => setPhoneVisibility("requests")}
                      className="text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm">Users who have requested (pending)</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="phoneVisibility"
                      value="nobody"
                      checked={phoneVisibility === "nobody"}
                      onChange={() => setPhoneVisibility("nobody")}
                      className="text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm">Nobody</span>
                  </label>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="flex gap-3 p-4 border-t border-[#E7E5E0]">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border border-[#E7E5E0] rounded-xl text-sm font-medium hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="flex-1 py-2.5 bg-purple-600 text-white rounded-xl text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </div>
    </div>
  );
}